<template>
  <div class="app-shell" :class="{ 'app-shell--with-nav': !!$slots.nav }">
    <header v-if="$slots.header" class="app-shell__header">
      <slot name="header" />
    </header>
    <main class="app-shell__main hide-scrollbar" ref="mainRef">
      <slot />
    </main>
    <nav v-if="$slots.nav" class="app-shell__nav glass-nav hairline-top">
      <slot name="nav" />
    </nav>
    <Teleport to="body">
      <div id="sheet-portal" />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
defineProps<{ scrollKey?: string | number }>();
const mainRef = ref<HTMLElement | null>(null);
defineExpose({ mainRef });
</script>

<style scoped lang="scss">
.app-shell {
  display: flex;
  flex-direction: column;
  // --app-height = реальная window.innerHeight (см. main.ts). Фолбэк 100dvh для SSR/раннего кадра.
  height: var(--app-height, 100dvh);
  width: 100%;
  // Mobile-frame layout — capped at 480px on wider viewports.
  // Body ambient gradients показывают вокруг frame на desktop; frame имеет тонкую
  // подсветку + hairline границу для luxury feel. Ниже breakpoint — full-viewport (mobile).
  max-width: min(100vw, 480px);
  margin-inline: auto;
  background: var(--bg-base);
  color: var(--text);
  overflow: hidden;

  @media (min-width: 481px) {
    box-shadow:
      0 0 0 1px var(--hairline, rgba(255, 255, 255, 0.08)),
      0 20px 60px -10px rgba(0, 0, 0, 0.55);
    // Slight scale-in — feels intentional vs stretched-full
    border-radius: 0;
  }
}

.app-shell__header {
  flex: 0 0 auto;
}

.app-shell__main {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  // Bottom safe-area belongs to the nav when it exists. Applying it to main as well produced a
  // second empty strip on iOS standalone and made full-height auth/PIN screens taller than their slot.
  padding-bottom: env(safe-area-inset-bottom);
}

.app-shell--with-nav .app-shell__main { padding-bottom: 0; }

.app-shell__nav {
  flex: 0 0 auto;
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
